import { createSelector, createEntityAdapter } from '@reduxjs/toolkit';
import { apiSlice } from './apiSlice';

const eventsAdapter = createEntityAdapter({
    selectId: (event) => event.id
});
const initialState = eventsAdapter.getInitialState();

export const eventsApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getEvents: builder.query({
            query: ({ pageSize, years, page, year, month, summary, statuses, statusMode }) => ({
                url: '/tapahtumat/paginated',
                params: {
                    pageSize,
                    years,
                    page,
                    year,
                    month,
                    summary,
                    statusMode,
                    statuses: Array.isArray(statuses) ? statuses.join(',') : statuses
                },
            }),
            providesTags: (result) => {
                const events = Array.isArray(result)
                    ? result
                    : Array.isArray(result?.tapahtumat)
                        ? result.tapahtumat
                        : [];

                return [
                    'Events',
                    ...events.map(({ id }) => ({ type: 'Events', id }))
                ];
            },
            keepUnusedDataFor: 60
        }),
        getEvent: builder.query({
            query: ({ id }) => ({
                url: `/tapahtumat/${id}`,
            }),
            providesTags: (result, error, arg) => [{ type: 'Events', id: arg.id }],
            keepUnusedDataFor: 30
        }),
        getAllLaskutettavat: builder.query({
            query: () => ({
                url: '/tapahtumat/laskutettavat'
            }),
            keepUnusedDataFor: 60
        }),
        getAllLaskutetetut: builder.query({
            query: () => ({
                url: '/tapahtumat/laskutetut'
            }),
            keepUnusedDataFor: 60
        }),
        getAllUserEvents: builder.query({
            query: () => ({
                url: '/tapahtumat/user/all'
            }),
            providesTags: ['Events'],
            keepUnusedDataFor: 60
        }),
        getTapahtumatByIds: builder.query({
            query: (ids) => ({
                url: '/tapahtumat/by-ids',
                method: 'POST',
                body: { ids }
            }),
            transformResponse: (response) => response.tapahtumat,
            keepUnusedDataFor: 0
        }),
        addNewEvent: builder.mutation({
            query: newEvent => ({
                url: '/tapahtumat',
                method: 'POST',
                body: newEvent
            }),
            invalidatesTags: ['Events']
        }),
        editEvent: builder.mutation({
            query: editedEvent => ({
                url: '/tapahtumat',
                method: 'PUT',
                body: editedEvent
            }),
            invalidatesTags: (result, error, arg) => [
                'Events',
                ...(arg?.id ? [{ type: 'Events', id: arg.id }] : [])
            ]
        }),
        deleteEvent: builder.mutation({
            query: ({ id }) => ({
                url: '/tapahtumat',
                method: 'DELETE',
                body: { id }
            }),
            invalidatesTags: ['Events']
        })
    })
});

export const {
    useGetEventsQuery,
    useGetEventQuery,
    useLazyGetEventQuery,
    useGetAllUserEventsQuery,
    useAddNewEventMutation,
    useEditEventMutation,
    useDeleteEventMutation,
    useGetAllLaskutettavatQuery,
    useGetAllLaskutetetutQuery,
    useGetTapahtumatByIdsQuery
} = eventsApiSlice;

// Create Selectors for Events.
export const selectEventsResult = eventsApiSlice.endpoints.getEvents.select('eventsList');

const selectEventsData = createSelector(
    selectEventsResult,
    eventsResult => eventsResult.data
);

export const {
    selectAll: selectAllEvents,
    selectById: selectEventById,
    selectIds: selectEventIds
} = eventsAdapter.getSelectors(state => selectEventsData(state) ?? initialState);

export const selectEventsByUser = createSelector(
    selectAllEvents,
    (state, userId) => userId,
    (events, userId) => events.filter((event => event.user === userId ))
);

export const selectPublishedEventsByUser = createSelector(
    selectAllEvents,
    (state, userId) => userId,
    (events, userId) => events.filter((event =>
        event.user === userId && event.status.toLowerCase() === 'julkaistu'
    ))
);

export const selectNonPublishedEventsByUser = createSelector(
    selectAllEvents,
    (state, userId) => userId,
    (events, userId) => events.filter((event =>
        event.user === userId && event.status.toLowerCase() === 'luonnos'
    ))
);